import React, { useCallback, useEffect, useState } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
  CODE,
  CHECK_LIST,
  HEADING,
  type ElementTransformer,
} from '@lexical/markdown'
import {
  HeadingNode,
  QuoteNode,
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  type HeadingTagType,
} from '@lexical/rich-text'
import {
  ListNode,
  ListItemNode,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from '@lexical/list'
import {
  LinkNode,
  AutoLinkNode,
  TOGGLE_LINK_COMMAND,
  $isLinkNode,
} from '@lexical/link'
import { $setBlocksType } from '@lexical/selection'
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
  type EditorState,
} from 'lexical'
import { mergeRegister, $getNearestNodeOfType } from '@lexical/utils'
import styles from './MarkdownEditor.module.scss'

// CommonMark permits up to 3 leading spaces before a heading; the default
// Lexical HEADING transformer is anchored with no leading whitespace, so it
// silently drops headings like "  ## Title" that `marked` renders fine.
const PERMISSIVE_HEADING: ElementTransformer = {
  dependencies: [HeadingNode],
  export: HEADING.export,
  regExp: /^\s{0,3}(#{1,6})\s/,
  replace: (parentNode, children, match) => {
    const tag = ('h' + match[1].length) as HeadingTagType
    const node = $createHeadingNode(tag)
    node.append(...children)
    parentNode.replace(node)
    node.select(0, 0)
  },
  type: 'element',
}

// Default TRANSFORMERS includes CODE/CHECK_LIST which require CodeNode and
// extra list setup we don't register. Swap in the permissive HEADING too.
const MARKDOWN_TRANSFORMERS = TRANSFORMERS.map((t) =>
  t === HEADING ? PERMISSIVE_HEADING : t,
).filter((t) => t !== CODE && t !== CHECK_LIST)

interface MarkdownEditorProps {
  /** Initial markdown content. Read once on mount; the editor is uncontrolled after that. */
  initialValue: string
  /** Fired with the current markdown serialization on every edit. */
  onChange: (markdown: string) => void
  placeholder?: string
  ariaLabel?: string
}

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'quote' | 'ul' | 'ol'

const editorTheme = {
  paragraph: styles.paragraph,
  heading: {
    h1: styles.h1,
    h2: styles.h2,
    h3: styles.h3,
  },
  quote: styles.quote,
  list: {
    ul: styles.ul,
    ol: styles.ol,
    listitem: styles.listitem,
  },
  link: styles.link,
  text: {
    bold: styles.bold,
    italic: styles.italic,
  },
}

const ToolbarPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [blockType, setBlockType] = useState<BlockType>('paragraph')
  const [isLink, setIsLink] = useState(false)

  const updateToolbarState = useCallback(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return

    setIsBold(selection.hasFormat('bold'))
    setIsItalic(selection.hasFormat('italic'))

    const anchorNode = selection.anchor.getNode()
    const element =
      anchorNode.getKey() === 'root'
        ? anchorNode
        : anchorNode.getTopLevelElementOrThrow()

    if ($isHeadingNode(element)) {
      const tag = element.getTag()
      if (tag === 'h1') setBlockType('h1')
      else if (tag === 'h2') setBlockType('h2')
      else if (tag === 'h3') setBlockType('h3')
      else setBlockType('paragraph')
    } else if (element.getType() === 'quote') {
      setBlockType('quote')
    } else if ($isListNode(element)) {
      setBlockType(element.getListType() === 'number' ? 'ol' : 'ul')
    } else {
      // List nodes wrap their items, so the anchor may be inside a list item.
      const parentList = $getNearestNodeOfType(anchorNode, ListNode)
      if (parentList) {
        setBlockType(parentList.getListType() === 'number' ? 'ol' : 'ul')
      } else {
        setBlockType('paragraph')
      }
    }

    const linkParent = $getNearestNodeOfType(anchorNode, LinkNode)
    setIsLink(linkParent !== null || $isLinkNode(anchorNode))
  }, [])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => updateToolbarState())
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbarState()
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
    )
  }, [editor, updateToolbarState])

  const formatHeading = useCallback(
    (tag: 'h1' | 'h2' | 'h3') => {
      editor.update(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return
        if (blockType === tag) {
          $setBlocksType(selection, () => $createParagraphNode())
        } else {
          $setBlocksType(selection, () => $createHeadingNode(tag))
        }
      })
    },
    [editor, blockType],
  )

  const formatQuote = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      if (blockType === 'quote') {
        $setBlocksType(selection, () => $createParagraphNode())
      } else {
        $setBlocksType(selection, () => $createQuoteNode())
      }
    })
  }, [editor, blockType])

  const toggleList = useCallback(
    (kind: 'ul' | 'ol') => {
      if (blockType === kind) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      } else if (kind === 'ul') {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
      } else {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
      }
    },
    [editor, blockType],
  )

  const toggleLink = useCallback(() => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
      return
    }
    const url = window.prompt('Enter URL', 'https://')
    if (!url) return
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
  }, [editor, isLink])

  return (
    <div className={styles.toolbar} role="toolbar" aria-label="Formatting">
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className={`${styles.toolbarBtn} ${isBold ? styles.active : ''}`}
        aria-pressed={isBold}
        aria-label="Bold"
        title="Bold (⌘B)"
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className={`${styles.toolbarBtn} ${isItalic ? styles.active : ''}`}
        aria-pressed={isItalic}
        aria-label="Italic"
        title="Italic (⌘I)"
      >
        <em>I</em>
      </button>
      <span className={styles.toolbarDivider} aria-hidden />
      <button
        type="button"
        onClick={() => formatHeading('h1')}
        className={`${styles.toolbarBtn} ${blockType === 'h1' ? styles.active : ''}`}
        aria-pressed={blockType === 'h1'}
        aria-label="Heading 1"
        title="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h2')}
        className={`${styles.toolbarBtn} ${blockType === 'h2' ? styles.active : ''}`}
        aria-pressed={blockType === 'h2'}
        aria-label="Heading 2"
        title="Heading 2"
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h3')}
        className={`${styles.toolbarBtn} ${blockType === 'h3' ? styles.active : ''}`}
        aria-pressed={blockType === 'h3'}
        aria-label="Heading 3"
        title="Heading 3"
      >
        H3
      </button>
      <span className={styles.toolbarDivider} aria-hidden />
      <button
        type="button"
        onClick={toggleLink}
        className={`${styles.toolbarBtn} ${isLink ? styles.active : ''}`}
        aria-pressed={isLink}
        aria-label="Link"
        title="Link"
      >
        Link
      </button>
      <span className={styles.toolbarDivider} aria-hidden />
      <button
        type="button"
        onClick={() => toggleList('ul')}
        className={`${styles.toolbarBtn} ${blockType === 'ul' ? styles.active : ''}`}
        aria-pressed={blockType === 'ul'}
        aria-label="Bulleted list"
        title="Bulleted list"
      >
        • List
      </button>
      <button
        type="button"
        onClick={() => toggleList('ol')}
        className={`${styles.toolbarBtn} ${blockType === 'ol' ? styles.active : ''}`}
        aria-pressed={blockType === 'ol'}
        aria-label="Numbered list"
        title="Numbered list"
      >
        1. List
      </button>
      <button
        type="button"
        onClick={formatQuote}
        className={`${styles.toolbarBtn} ${blockType === 'quote' ? styles.active : ''}`}
        aria-pressed={blockType === 'quote'}
        aria-label="Blockquote"
        title="Blockquote"
      >
        ❝
      </button>
    </div>
  )
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  initialValue,
  onChange,
  placeholder = 'Write something…',
  ariaLabel = 'Markdown body',
}) => {
  // Capture initial value once. Subsequent changes to the prop are ignored;
  // the editor is the source of truth after mount.
  const [seed] = useState(initialValue)

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const md = $convertToMarkdownString(MARKDOWN_TRANSFORMERS)
        onChange(md)
      })
    },
    [onChange],
  )

  const initialConfig = {
    namespace: 'MarkdownEditor',
    theme: editorTheme,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
    editorState: () => $convertFromMarkdownString(seed || '', MARKDOWN_TRANSFORMERS),
    onError: (error: Error) => {
      console.error('Lexical editor error:', error)
    },
  }

  return (
    <div className={styles.editorShell}>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className={styles.editorBody}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={styles.contentEditable}
                aria-label={ariaLabel}
              />
            }
            placeholder={<div className={styles.placeholder}>{placeholder}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <MarkdownShortcutPlugin transformers={MARKDOWN_TRANSFORMERS} />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      </LexicalComposer>
    </div>
  )
}

export default MarkdownEditor
